import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { supabase } from '../supabaseClient';
import { pct, formatCurrency, isOverdue } from '../utils';
import { STATUS_COLORS, CONTACT_TYPE_COLORS } from '../constants';
import DashboardKpiTiles from './DashboardKpiTiles';
import DashboardDonutChart from './DashboardDonutChart';
import DashboardBudgetSummary from './DashboardBudgetSummary';
import DashboardChangeOrders from './DashboardChangeOrders';
import DashboardVendorInsurance from './DashboardVendorInsurance';
import DashboardDeadlines from './DashboardDeadlines';
import DashboardActivity from './DashboardActivity';
import DashboardTeam from './DashboardTeam';
import DashboardActionItems from './DashboardActionItems';
import GettingStarted from './GettingStarted';

const NLQueryPanel = lazy(() => import('./NLQueryPanel'));
const CalendarView = lazy(() => import('./CalendarView'));

const TAB_CONFIG = [
  { key: "projects", label: "Projects" },
  { key: "action-items", label: "Action Items" },
  { key: "insights", label: "Insights" },
  { key: "activity", label: "Activity" },
  { key: "team", label: "Team" },
];

const SORT_OPTIONS = [
  ["newest", "Newest First"],
  ["oldest", "Oldest First"],
  ["name-az", "Name A\u2013Z"],
  ["name-za", "Name Z\u2013A"],
  ["progress-high", "Progress (High\u2013Low)"],
  ["progress-low", "Progress (Low\u2013High)"],
  ["budget-high", "Budget (High\u2013Low)"],
];

const FILTER_OPTIONS = [
  ["all", "All"],
  ["land", "Land"],
  ["construction", "Construction"],
  ["spec", "Spec Build"],
  ["custom", "Custom Build"],
];

export default function Dashboard({ projects, contacts, enabledFeatures, projectGroups, session, hasApiKey, companyProfile, onApproveChangeOrder, onUpdateTask, onCreateGroup, onRenameGroup, onDeleteGroup, onAssignGroup, onOpen, onNew, onContacts, onReports, onSettings, onHelp, onSignOut, onUnarchive, onDeleteArchived, onNavigate }) {
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dashboardTab, setDashboardTab] = useState(() => sessionStorage.getItem('baulit_dashboardTab') || "projects");
  const [aiMode, setAiMode] = useState(false);
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem('baulit_sort') || "newest");
  const [filterType, setFilterType] = useState(() => sessionStorage.getItem('baulit_filter') || "all");
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('baulit_viewMode') || "flat");
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('baulit_collapsedGroups') || '{}'); } catch { return {}; }
  });
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [confirmDeleteArchivedId, setConfirmDeleteArchivedId] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => localStorage.getItem("baulit_onboardingDismissed") === "true");

  function handleSortChange(val) { setSortBy(val); sessionStorage.setItem('baulit_sort', val); }
  function handleFilterChange(val) { setFilterType(val); sessionStorage.setItem('baulit_filter', val); }
  function handleViewModeChange(mode) { setViewMode(mode); sessionStorage.setItem('baulit_viewMode', mode); }
  function handleTabChange(tab) { setDashboardTab(tab); sessionStorage.setItem('baulit_dashboardTab', tab); }

  function handleWidgetNavigate(type, id) {
    if (type === "overdue-blocked") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
    else if (type === "pending") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
    else if (type === "punchList") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
    else if (type === "projects-tab") handleTabChange("projects");
    else if (type === "budget-risk") { if (onNavigate) onNavigate("budget-risk"); else onReports(); }
    else if (type === "budget-detail" && id) { if (onNavigate) onNavigate("budget-detail", id); }
    else if (type === "task-detail" && id) { if (onNavigate) onNavigate("open-project", id, "tasks"); else onOpen(id); }
    else if (type === "change-order" && id) { if (onNavigate) onNavigate("open-project", id, "changes"); else onOpen(id); }
    else if (type === "change-orders") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
    else if (type === "daily-log" && id) { if (onNavigate) onNavigate("open-project", id, "logs"); else onOpen(id); }
    else if (type === "files" && id) { if (onNavigate) onNavigate("open-project", id, "files"); else onOpen(id); }
    else if (type === "vendor-insurance") { if (onNavigate) onNavigate("vendor-insurance"); else onReports(); }
    else if (type === "contact-detail") { onContacts(); }
    else if (type === "contacts") { onContacts(); }
    else if (type === "unassigned-tasks" && id) { if (onNavigate) onNavigate("open-project", id, "tasks"); else onOpen(id); }
    else if (type === "filter-status") {
      const statusKey = id;
      if (statusKey === "Blocked") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
      else if (statusKey === "punchList") handleTabChange("projects");
      else if (statusKey === "Pending Approval") { if (onNavigate) onNavigate("overdue-blocked"); else onReports(); }
      else handleTabChange("projects");
    }
  }
  function toggleGroupCollapse(groupId) {
    setCollapsedGroups(prev => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      sessionStorage.setItem('baulit_collapsedGroups', JSON.stringify(next));
      return next;
    });
  }

  async function loadArchivedProjects() {
    setArchivedLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("id, name, type, subtype, address, created_at")
      .eq("archived", true)
      .order("created_at", { ascending: false });
    if (error) console.error("Failed to load archived projects:", error);
    setArchivedProjects(data || []);
    setArchivedLoading(false);
  }

  function handleShowArchived() {
    if (showArchived) {
      setShowArchived(false);
      return;
    }
    setShowArchived(true);
    loadArchivedProjects();
  }

  async function handleUnarchive(id) {
    await onUnarchive(id);
    setArchivedProjects(ps => ps.filter(p => p.id !== id));
  }

  async function handleDeleteArchived(id) {
    await onDeleteArchived(id);
    setArchivedProjects(ps => ps.filter(p => p.id !== id));
  }

  // View-layer gate: when every project a user has access to is a stakeholder role,
  // suppress admin-oriented UI (tabs, filter chips). RLS remains the security boundary.
  const isStakeholderOnly = projects.length > 0 && projects.every(p => p.role === "stakeholder");

  // Split projects by role — admin/manager projects get full controls, invited projects get simplified cards
  const { adminProjects, invitedProjects, totalPending, pendingProjects, displayProjects } = useMemo(() => {
    const admin = projects.filter(p => p.role === "admin" || p.role === "manager" || !p.role);
    const invited = projects.filter(p => p.role === "contractor" || p.role === "stakeholder");
    const tp = admin.reduce((sum, p) => sum + (p.tasks || []).filter(t => t.status === "Pending Approval").length, 0);
    const pp = admin.filter(p => (p.tasks || []).some(t => t.status === "Pending Approval"));
    // Apply filter and sort to admin projects
    let list = [...admin];
    if (filterType === "land") list = list.filter(p => p.type === "land");
    else if (filterType === "construction") list = list.filter(p => p.type === "construction");
    else if (filterType === "spec") list = list.filter(p => p.type === "construction" && p.subtype === "spec");
    else if (filterType === "custom") list = list.filter(p => p.type === "construction" && p.subtype === "custom");
    const sumBudget = p => (p.tasks || []).reduce((s, t) => s + (t.estimatedCost || 0), 0);
    switch (sortBy) {
      case "name-az": list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "name-za": list.sort((a, b) => b.name.localeCompare(a.name)); break;
      case "newest": list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); break;
      case "oldest": list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); break;
      case "progress-high": list.sort((a, b) => pct(b.tasks || []) - pct(a.tasks || [])); break;
      case "progress-low": list.sort((a, b) => pct(a.tasks || []) - pct(b.tasks || [])); break;
      case "budget-high": list.sort((a, b) => sumBudget(b) - sumBudget(a)); break;
    }
    return { adminProjects: admin, invitedProjects: invited, totalPending: tp, pendingProjects: pp, displayProjects: list };
  }, [projects, filterType, sortBy]);

  const groupedProjects = useMemo(() => {
    if (viewMode !== "grouped" || !projectGroups || projectGroups.length === 0) return null;
    const groups = projectGroups.map(g => ({
      ...g,
      projects: displayProjects.filter(p => p.groupId === g.id),
    }));
    const ungrouped = displayProjects.filter(p => !p.groupId);
    return { groups, ungrouped };
  }, [displayProjects, projectGroups, viewMode]);

  const actionItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const items = [];

    for (const p of adminProjects) {
      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader) continue;
        if (t.status === "Complete") continue;
        if (t.dueDate && new Date(t.dueDate + "T12:00:00") < today) {
          const diffDays = Math.round((today - new Date(t.dueDate + "T12:00:00")) / 86400000);
          items.push({
            type: "overdue", priority: 1,
            taskId: t.id, taskName: t.name,
            projectId: p.id, projectName: p.name,
            detail: `${diffDays} day${diffDays !== 1 ? "s" : ""} overdue`,
            dueDate: t.dueDate, diffDays,
            assignedTo: t.assignedTo,
          });
        }
      }

      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader || t.status !== "Blocked") continue;
        items.push({
          type: "blocked", priority: 2,
          taskId: t.id, taskName: t.name,
          projectId: p.id, projectName: p.name,
          detail: t.rejectionNote || "No blocker note",
          assignedTo: t.assignedTo,
        });
      }

      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader || t.status !== "Pending Approval") continue;
        items.push({
          type: "pending", priority: 3,
          taskId: t.id, taskName: t.name,
          projectId: p.id, projectName: p.name,
          detail: t.assignedTo ? `From ${t.assignedTo}` : "Unassigned",
          assignedTo: t.assignedTo,
          assignedUserId: t.assignedUserId || null,
        });
      }

      for (const co of (p.changeOrders || [])) {
        if (co.status !== "submitted") continue;
        items.push({
          type: "change-order", priority: 4,
          coId: co.id, coTitle: co.title || `CO #${co.coNumber}`,
          projectId: p.id, projectName: p.name,
          detail: co.delta != null ? formatCurrency(co.delta) : "No amount",
          delta: co.delta,
        });
      }

      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader || t.status === "Complete") continue;
        if (!t.assignedTo && !t.assignedUserId) {
          items.push({
            type: "unassigned", priority: 6,
            taskId: t.id, taskName: t.name,
            projectId: p.id, projectName: p.name,
            detail: t.dueDate ? `Due ${t.dueDate}` : "No due date",
          });
        }
      }
    }

    for (const c of (contacts || [])) {
      if (!c.wcExpires) continue;
      const exp = new Date(c.wcExpires + "T12:00:00");
      exp.setHours(0, 0, 0, 0);
      const daysLeft = Math.round((exp - today) / 86400000);
      if (daysLeft <= 30) {
        items.push({
          type: "insurance", priority: 5,
          contactName: c.name, company: c.company,
          detail: daysLeft < 0 ? "Expired" : `Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`,
          daysLeft,
        });
      }
    }

    items.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      if (a.type === "overdue") return (b.diffDays || 0) - (a.diffDays || 0);
      return 0;
    });

    return items;
  }, [adminProjects, contacts]);

  const effectiveViewMode = (viewMode === "calendar" && enabledFeatures?.calendar === false) ? "flat"
    : (viewMode === "grouped" && (!projectGroups || projectGroups.length === 0)) ? "flat"
    : viewMode;

  // Stakeholder-only users can only reach the Projects tab — force stale persisted tabs back.
  const effectiveDashboardTab = (isStakeholderOnly && dashboardTab !== "projects") ? "projects" : dashboardTab;

  // Keep sessionStorage in sync so reloads don't flicker the hidden tab before re-derivation.
  useEffect(() => {
    if (isStakeholderOnly && dashboardTab !== "projects") {
      sessionStorage.setItem('baulit_dashboardTab', 'projects');
    }
  }, [isStakeholderOnly, dashboardTab]);


  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;
  const matchedProjects = isSearching ? projects.filter(p => p.name.toLowerCase().includes(query) || (p.address || "").toLowerCase().includes(query)) : [];
  const matchedTasks = isSearching ? projects.flatMap(p => (p.tasks || []).filter(t => t.name.toLowerCase().includes(query) || (t.assignedTo || "").toLowerCase().includes(query)).map(t => ({ ...t, projectId: p.id, projectName: p.name }))) : [];
  const matchedContacts = isSearching ? contacts.filter(c => c.name.toLowerCase().includes(query) || (c.company || "").toLowerCase().includes(query) || (c.email || "").toLowerCase().includes(query)) : [];
  const hasResults = matchedProjects.length > 0 || matchedTasks.length > 0 || matchedContacts.length > 0;

  function renderProjectCard(p) {
    const done = pct(p.tasks);
    const subtypeLabel = p.type === "construction" ? (p.subtype === "custom" ? "Custom Build" : "Spec Build") : null;
    const pendingCount = (p.tasks || []).filter(t => t.status === "Pending Approval").length;
    const blockedCount = (p.tasks || []).filter(t => t.status === "Blocked").length;
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const overdueCount = (p.tasks || []).filter(t => isOverdue(t, now)).length;
    const completedCount = p.tasks.filter(t => t.status === "Complete").length;
    return (
      <div key={p.id} onClick={() => onOpen(p.id)} className="bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-slate-300 transition-all">
        {/* Navy accent header — echoes the ProjectView navy header */}
        <div className="bg-brand-navy px-5 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-white text-base truncate" title={p.name}>{p.name}</h3>
            <p className="text-xs text-white/50 truncate">{p.address || "No address"}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.type === "land" ? "bg-emerald-600 text-white" : "bg-blue-600 text-white"}`}>
              {p.type === "land" ? "Land Purchase" : "Construction"}
            </span>
            {subtypeLabel && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/70">{subtypeLabel}</span>
            )}
          </div>
        </div>
        {/* White content area */}
        <div className="px-5 py-3">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{completedCount} of {p.tasks.length} tasks complete</span>
            <span className="font-semibold text-stone-700">{done}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mb-2">
            <div className="bg-amber-400 h-2 rounded-full transition-all" style={{ width: `${done}%` }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {pendingCount > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onNavigate("open-project", p.id, "tasks", "Pending Approval"); }}
                className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors">{pendingCount} pending review</button>
            )}
            {overdueCount > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onNavigate("open-project", p.id, "tasks"); }}
                className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors">{overdueCount} overdue</button>
            )}
            {blockedCount > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onNavigate("open-project", p.id, "tasks", "Blocked"); }}
                className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors">{blockedCount} blocked</button>
            )}
            {(p.start || p.target) && (
              <span className="text-xs text-slate-400">
                {p.start ? `Start: ${new Date(p.start + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}{p.start && p.target ? " · " : ""}{p.target ? `Target: ${new Date(p.target + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
              </span>
            )}
            {p.createdAt && <span className="text-xs text-slate-400 ml-auto">{new Date(p.createdAt + (p.createdAt.includes("T") ? "" : "T12:00:00")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
          </div>
          {projectGroups && projectGroups.length > 0 && (
            <select
              value={p.groupId || ""}
              onClick={e => e.stopPropagation()}
              onChange={e => { e.stopPropagation(); onAssignGroup(p.id, e.target.value || null); }}
              className="text-xs border border-slate-200 rounded px-2 py-0.5 bg-white text-slate-500 mt-2 w-full focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              <option value="">No group</option>
              {projectGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/baulit-logo-dark.svg" alt="Baulit" className="h-9 hidden sm:block" />
          <img src="/baulit-icon.svg" alt="Baulit" className="h-8 sm:hidden" />
        </div>
        <div className="flex gap-2 items-center">

          {!isStakeholderOnly && (
            <button onClick={onNew} className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2 rounded-lg">+ New Project</button>
          )}
          <button onClick={onHelp} className="bg-white/10 hover:bg-white/20 text-white text-sm font-semibold w-9 h-9 rounded-lg flex items-center justify-center" title="Help & Support">?</button>
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="bg-white/10 hover:bg-white/20 text-white text-sm font-semibold w-9 h-9 rounded-lg flex items-center justify-center" title="Menu">⚙</button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50">
                {enabledFeatures?.reports !== false && (
                  <button onClick={() => { setShowMenu(false); onReports(); }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Reports</button>
                )}
                {enabledFeatures?.contacts !== false && (
                  <button onClick={() => { setShowMenu(false); onContacts(); }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Contacts</button>
                )}
                <button onClick={() => { setShowMenu(false); onSettings(); }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Settings</button>
                <button onClick={() => { setShowMenu(false); onHelp(); }} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Help</button>
                <button onClick={() => { setShowMenu(false); onSignOut(); }} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">Sign Out</button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-6">

        {/* Summary Banner */}
        {totalPending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <span className="text-amber-500 text-2xl leading-none">!</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">{totalPending} task{totalPending !== 1 ? "s" : ""} awaiting your review</p>
              <p className="text-xs text-amber-600">Across {pendingProjects.length} project{pendingProjects.length !== 1 ? "s" : ""}: {pendingProjects.map((p, i) => (
                <span key={p.id}>{i > 0 && ", "}<button onClick={() => onOpen(p.id)} className="underline hover:text-amber-800 font-medium">{p.name}</button></span>
              ))}</p>
            </div>
          </div>
        )}

        {/* Search / Ask Baulit Toggle */}
        <div className="mb-4">
          {enabledFeatures?.nlQuery !== false && (
            <div className="flex gap-1 mb-2">
              <button onClick={() => { setAiMode(false); setSearchQuery(""); }}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${!aiMode ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
                Search
              </button>
              <button onClick={() => { setAiMode(true); setSearchQuery(""); }}
                className={`text-xs px-3 py-1 rounded-full border transition-colors flex items-center gap-1 ${aiMode ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"} ${!hasApiKey ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={!hasApiKey}
                title={!hasApiKey ? "Connect your API key in Settings to use Ask Baulit" : "Ask questions about your data"}>
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
                Ask Baulit
              </button>
              <span className="text-[10px] text-slate-400 ml-1 self-center">{aiMode ? "AI-powered answers about your data" : "Filter projects by name"}</span>
            </div>
          )}
          {aiMode ? (
            <Suspense fallback={<div className="bg-white border border-slate-200 rounded-lg p-4 text-center text-sm text-slate-400">Loading...</div>}>
              <NLQueryPanel
                session={session}
                hasApiKey={hasApiKey}
                projects={projects}
                contacts={contacts}
                projectGroups={projectGroups}
              />
            </Suspense>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search projects, tasks, contacts..."
                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-9 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">&#x1F50D;</span>
              {isSearching && (
                <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm" aria-label="Clear search">&#x2715;</button>
              )}
            </div>
          )}
        </div>

        {/* Dashboard Tab Strip — hidden for stakeholder-only users (they only see Projects) */}
        {!isStakeholderOnly && (
          <div className="border-b border-slate-200 -mx-6 px-2 mb-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex overflow-x-auto scrollbar-hide">
                {TAB_CONFIG.map(tab => (
                  <button key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`whitespace-nowrap text-sm font-medium px-4 py-2.5 border-b-2 transition-colors ${
                      effectiveDashboardTab === tab.key
                        ? "border-amber-500 text-stone-800 font-semibold"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                    }`}>
                    {tab.label}
                    {tab.key === "action-items" && actionItems.length > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center inline-flex items-center justify-center">
                        {actionItems.length > 99 ? "99+" : actionItems.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {isSearching ? (
          <div>
            {!hasResults && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <p className="text-slate-400 text-sm">No results for "{searchQuery.trim()}"</p>
              </div>
            )}
            {matchedProjects.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Projects ({matchedProjects.length})</h3>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {matchedProjects.map(p => (
                    <div key={p.id} onClick={() => onOpen(p.id)} className="px-4 py-3 cursor-pointer hover:bg-slate-50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-stone-800">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.address || "No address"} &middot; {pct(p.tasks)}% complete</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.type === "land" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                        {p.type === "land" ? "Land" : "Construction"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {matchedTasks.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tasks ({matchedTasks.length})</h3>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {matchedTasks.slice(0, 20).map((t, i) => (
                    <div key={t.id + "-" + i} onClick={() => onOpen(t.projectId)} className="px-4 py-3 cursor-pointer hover:bg-slate-50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-stone-800">{t.name}</p>
                        <p className="text-xs text-slate-500">{t.projectName}{t.assignedTo ? ` \u00b7 ${t.assignedTo}` : ""}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status] || "bg-slate-100 text-slate-600"}`}>
                        {t.status}
                      </span>
                    </div>
                  ))}
                  {matchedTasks.length > 20 && (
                    <p className="px-4 py-2 text-xs text-slate-400">Showing 20 of {matchedTasks.length} results</p>
                  )}
                </div>
              </div>
            )}
            {matchedContacts.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Contacts ({matchedContacts.length})</h3>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {matchedContacts.map(c => (
                    <div key={c.id} onClick={onContacts} className="px-4 py-3 cursor-pointer hover:bg-slate-50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-stone-800">{c.name}</p>
                        <p className="text-xs text-slate-500">{c.company ? c.company + " \u00b7 " : ""}{c.email || c.phone || ""}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CONTACT_TYPE_COLORS[c.type] || "bg-slate-100 text-slate-600"}`}>
                        {c.type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : effectiveDashboardTab === "action-items" ? (
          <DashboardActionItems
            items={actionItems}
            onNavigate={handleWidgetNavigate}
            onApproveChangeOrder={onApproveChangeOrder}
            onUpdateTask={onUpdateTask}
            session={session}
          />
        ) : effectiveDashboardTab === "insights" ? (
          projects.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <p className="text-slate-400 text-sm">Create a project to see insights here.</p>
          </div>
          ) : <div className="space-y-4">
            <DashboardKpiTiles projects={adminProjects} onNavigate={handleWidgetNavigate} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DashboardDonutChart projects={adminProjects} onFilterStatus={status => handleWidgetNavigate("filter-status", status)} />
              <DashboardBudgetSummary projects={adminProjects} enabledFeatures={enabledFeatures} onNavigate={handleWidgetNavigate} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DashboardChangeOrders projects={adminProjects} onNavigate={handleWidgetNavigate} />
              <DashboardVendorInsurance contacts={contacts} onNavigate={handleWidgetNavigate} />
            </div>
            <DashboardDeadlines projects={adminProjects} onNavigate={handleWidgetNavigate} />
          </div>
        ) : effectiveDashboardTab === "activity" ? (
          <DashboardActivity projects={adminProjects} onNavigate={handleWidgetNavigate} />
        ) : effectiveDashboardTab === "team" ? (
          <DashboardTeam projects={adminProjects} contacts={contacts} onNavigate={handleWidgetNavigate} />
        ) : (
          <>
        {/* Sort & Filter Bar — hidden for stakeholder-only users */}
        {!isStakeholderOnly && (
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <div className="flex gap-1 flex-wrap">
              {FILTER_OPTIONS.map(([val, label]) => (
                <button key={val} onClick={() => handleFilterChange(val)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    filterType === val
                      ? "bg-brand-navy text-white border-brand-navy"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                  }`}>{label}</button>
              ))}
            </div>
            <div className="ml-auto flex gap-2 items-center">
              {(projectGroups?.length > 0 || enabledFeatures?.calendar !== false) && (
                <div className="flex gap-0.5 border border-slate-200 rounded-lg overflow-hidden">
                  <button onClick={() => handleViewModeChange("flat")}
                    className={`text-xs px-3 py-1.5 transition-colors ${viewMode === "flat" ? "bg-brand-navy text-white" : "bg-white text-slate-500 hover:text-slate-700"}`}>List</button>
                  {projectGroups?.length > 0 && (
                    <button onClick={() => handleViewModeChange("grouped")}
                      className={`text-xs px-3 py-1.5 transition-colors ${viewMode === "grouped" ? "bg-brand-navy text-white" : "bg-white text-slate-500 hover:text-slate-700"}`}>Groups</button>
                  )}
                  {enabledFeatures?.calendar !== false && (
                    <button onClick={() => handleViewModeChange("calendar")}
                      className={`text-xs px-3 py-1.5 transition-colors ${viewMode === "calendar" ? "bg-brand-navy text-white" : "bg-white text-slate-500 hover:text-slate-700"}`}>Calendar</button>
                  )}
                </div>
              )}
              <button onClick={() => setShowGroupManager(true)}
                className="text-xs text-slate-500 hover:text-amber-600 font-medium">Manage Groups</button>
              {effectiveViewMode !== "calendar" && (
                <select value={sortBy} onChange={e => handleSortChange(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400">
                  {SORT_OPTIONS.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {!isStakeholderOnly && (
          <h2 className="text-lg font-semibold text-slate-700 mb-4">
            {filterType === "all" ? "All Projects" :
             filterType === "land" ? "Land Projects" :
             filterType === "construction" ? "Construction Projects" :
             filterType === "spec" ? "Spec Builds" :
             filterType === "custom" ? "Custom Builds" : "All Projects"}
            {filterType !== "all" && ` (${displayProjects.length})`}
          </h2>
        )}
        {isStakeholderOnly ? null : effectiveViewMode === "calendar" ? (
          <Suspense fallback={<div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">Loading calendar...</div>}>
            <CalendarView projects={displayProjects} companyAddress={companyProfile ? [companyProfile.addressLine1, `${companyProfile.city || ""}, ${companyProfile.state || ""} ${companyProfile.zip || ""}`].filter(Boolean).join(", ").trim() : ""}
              onOpenTask={(t) => { if (t._pId) onNavigate("open-project", t._pId, "tasks"); }}
              onOpenLog={(date, pId) => { if (pId) onNavigate("open-project", pId, "logs"); }} />
          </Suspense>
        ) : projects.length === 0 && !onboardingDismissed ? (
          <GettingStarted
            projects={projects}
            onNew={onNew}
            onNavigate={onNavigate}
            onOpen={onOpen}
            onDismiss={() => { localStorage.setItem("baulit_onboardingDismissed", "true"); setOnboardingDismissed(true); }}
          />
        ) : adminProjects.length === 0 && invitedProjects.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <p className="text-slate-400 text-sm mb-3">No projects yet.</p>
            <button onClick={onNew} className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2 rounded-lg">Create your first project</button>
          </div>
        ) : displayProjects.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <p className="text-slate-400 text-sm">No projects match this filter.</p>
            <button onClick={() => handleFilterChange("all")} className="text-amber-600 text-sm font-semibold mt-2 hover:text-amber-700">Show all projects</button>
          </div>
        ) : groupedProjects ? (
          <div className="space-y-4">
            {groupedProjects.groups.map(group => (
              <div key={group.id}>
                <button onClick={() => toggleGroupCollapse(group.id)}
                  className="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-2 hover:text-amber-600">
                  <span className="text-xs text-slate-400">{collapsedGroups[group.id] ? "\u25B6" : "\u25BC"}</span>
                  {group.name}
                  <span className="text-xs font-normal text-slate-400">({group.projects.length})</span>
                </button>
                {!collapsedGroups[group.id] && group.projects.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {group.projects.map(p => renderProjectCard(p))}
                  </div>
                )}
                {!collapsedGroups[group.id] && group.projects.length === 0 && (
                  <p className="text-xs text-slate-400 pl-5 mb-2">No projects in this group.</p>
                )}
              </div>
            ))}
            {groupedProjects.ungrouped.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-500 mb-2">Ungrouped ({groupedProjects.ungrouped.length})</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groupedProjects.ungrouped.map(p => renderProjectCard(p))}
                </div>
              </div>
            )}
          </div>
        ) : adminProjects.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <p className="text-slate-400 text-sm mb-3">No projects yet.</p>
            <button onClick={onNew} className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2 rounded-lg">Create your first project</button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {displayProjects.map(p => renderProjectCard(p))}
          </div>
        )}

        {/* Shared With Me — invited projects (contractor/stakeholder role) */}
        {invitedProjects.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-700 mb-4">Shared With Me</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {invitedProjects.map(p => {
                const userId = session?.user?.id;
                const myTaskCount = (p.tasks || []).filter(t => t.assignedUserId === userId).length;
                const totalTasks = (p.tasks || []).length;
                const done = totalTasks > 0 ? Math.round((p.tasks || []).filter(t => t.status === "Complete").length / totalTasks * 100) : 0;
                return (
                  <div key={p.id} onClick={() => onOpen(p.id)} className="bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-slate-300 transition-all">
                    <div className="bg-slate-700 px-5 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white text-base truncate" title={p.name}>{p.name}</h3>
                        <p className="text-xs text-white/50 truncate">{p.address || "No address"}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        p.role === "contractor" ? "bg-amber-500/80 text-white" : "bg-blue-500/80 text-white"
                      }`}>
                        {p.role === "contractor" ? "Contractor" : "Stakeholder"}
                      </span>
                    </div>
                    <div className="px-5 py-3">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>{p.role === "contractor" ? `${myTaskCount} task${myTaskCount !== 1 ? "s" : ""} assigned to you` : `${totalTasks} tasks`}</span>
                        <span className="font-semibold text-stone-700">{done}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-amber-400 h-2 rounded-full transition-all" style={{ width: `${done}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Archived Projects Section */}
      {onUnarchive && (
        <div className="max-w-5xl mx-auto px-4 mt-2 mb-4">
          <button onClick={handleShowArchived}
            className="text-xs text-slate-400 hover:text-slate-600 font-medium">
            {showArchived ? "Hide Archived" : "View Archived Projects"}
            {archivedProjects.length > 0 && showArchived && (
              <span className="ml-1 text-slate-300">({archivedProjects.length})</span>
            )}
          </button>

          {showArchived && (
            <div className="mt-3">
              {archivedLoading ? (
                <div className="text-xs text-slate-400 animate-pulse">Loading archived projects...</div>
              ) : archivedProjects.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No archived projects.</p>
              ) : (
                <div className="space-y-2">
                  {archivedProjects.map(p => (
                    <div key={p.id} className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3 opacity-75">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-600 truncate block">{p.name}</span>
                        <span className="text-xs text-slate-400">
                          {p.type === "land" ? "Land" : p.subtype === "spec" ? "Spec Build" : "Custom Build"}
                          {p.address && <span className="ml-2">{p.address}</span>}
                        </span>
                      </div>
                      <button onClick={() => handleUnarchive(p.id)}
                        className="text-xs text-green-600 hover:text-green-700 font-semibold px-2 py-1 rounded hover:bg-green-50">
                        Restore
                      </button>
                      {confirmDeleteArchivedId === p.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-red-600">Delete?</span>
                          <button onClick={() => { handleDeleteArchived(p.id); setConfirmDeleteArchivedId(null); }}
                            className="text-xs bg-red-500 text-white px-2 py-0.5 rounded font-medium">Yes</button>
                          <button onClick={() => setConfirmDeleteArchivedId(null)}
                            className="text-xs text-slate-400">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDeleteArchivedId(p.id)}
                          className="text-xs text-slate-400 hover:text-red-500 px-2 py-1 rounded hover:bg-red-50">
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Group Manager Modal */}
      {showGroupManager && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-stone-800 mb-4">Manage Groups</h2>
            {projectGroups && projectGroups.length > 0 ? (
              <div className="space-y-2 mb-4">
                {projectGroups.map(g => (
                  <div key={g.id} className="flex items-center gap-2">
                    {editingGroupId === g.id ? (
                      <>
                        <input autoFocus value={editingGroupName}
                          onChange={e => setEditingGroupName(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && editingGroupName.trim()) { onRenameGroup(g.id, editingGroupName.trim()); setEditingGroupId(null); } if (e.key === "Escape") setEditingGroupId(null); }}
                          className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                        <button onClick={() => { if (editingGroupName.trim()) { onRenameGroup(g.id, editingGroupName.trim()); } setEditingGroupId(null); }} className="text-xs text-amber-600 font-semibold hover:text-amber-700">Save</button>
                        <button onClick={() => setEditingGroupId(null)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-stone-700 font-medium">{g.name}</span>
                        <span className="text-xs text-slate-400">{projects.filter(p => p.groupId === g.id).length} projects</span>
                        <button onClick={() => { setEditingGroupId(g.id); setEditingGroupName(g.name); }} className="text-xs text-slate-500 hover:text-amber-600">Rename</button>
                        <button onClick={() => onDeleteGroup(g.id)} className="text-xs text-slate-500 hover:text-red-500">Delete</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 mb-4">No groups yet. Create one below to organize your projects.</p>
            )}
            <div className="flex gap-2">
              <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && newGroupName.trim()) { onCreateGroup(newGroupName.trim()); setNewGroupName(""); } }}
                placeholder="New group name..."
                className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <button onClick={() => { if (newGroupName.trim()) { onCreateGroup(newGroupName.trim()); setNewGroupName(""); } }}
                disabled={!newGroupName.trim()}
                className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50">Add</button>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => { setShowGroupManager(false); setEditingGroupId(null); }} className="text-sm text-slate-500 hover:text-slate-700 font-medium">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
